import { useState, useEffect, useRef } from 'react';
import { Utensils, Activity, Target, MessageSquare, CalendarCheck, CalendarPlus, HeartPulse, BarChart2, Apple } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { StatCard } from './StatCard';
import { HeroBanner } from './HeroBanner';
import { BookTrainingCard } from './BookTrainingCard';
import { CoachHubCard } from './CoachHubCard';
import { EvolutionChart } from './EvolutionChart';
import { CardioRing } from './CardioRing';
import { UserProfile, HistoryEntry } from '@/types/fitness';
import { supabase } from '@/integrations/supabase/client';
import { cachedQuery } from '@/lib/offlineStorage';
import { getTodayLocalString } from '@/lib/dateUtils';
import { celebrateOnce, fireGoalConfetti, todayKey } from '@/lib/celebrate';

interface DashboardSectionProps {
  perfil: UserProfile;
  ultimoRegistro: HistoryEntry;
  grasaFaltante: string;
  /** ID of the user whose dashboard this is. When provided, the profile
   *  avatar is shown at the top. */
  userId?: string;
  /** Display name for the avatar header. Falls back to perfil.nombreMeta. */
  displayName?: string;
  /** True when the user is viewing their own profile (allows editing the
   *  avatar). False when the coach is viewing a client (read-only). */
  isOwnProfile?: boolean;
}

export function CardioGoalCard({ meta, userId, cardioKcal }: { meta: number; userId?: string; cardioKcal?: number }) {
  const [fetched, setFetched] = useState<number | null>(null);
  const [monthAccum, setMonthAccum] = useState<number>(0);

  useEffect(() => {
    if (!userId) return;
    const today = getTodayLocalString();
    // Hoy — cacheado para que offline muestre el cardio del día
    // sin tener que esperar la red.
    if (cardioKcal === undefined) {
      cachedQuery<{ calories_burned: number }[]>(
        `cardio_today_${userId}_${today}`,
        () => supabase
          .from('exercise_logs')
          .select('calories_burned')
          .eq('user_id', userId)
          .eq('exercise_type', 'cardio')
          .eq('log_date', today)
      ).then(({ data }) => {
        if (data) setFetched(data.reduce((s, r) => s + Number(r.calories_burned), 0));
      });
    }
    // Acumulado del mes en curso (desde día 1) — cacheado.
    const now = new Date();
    const firstOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    cachedQuery<{ calories_burned: number }[]>(
      `cardio_month_${userId}_${firstOfMonth}_${today}`,
      () => supabase
        .from('exercise_logs')
        .select('calories_burned')
        .eq('user_id', userId)
        .eq('exercise_type', 'cardio')
        .gte('log_date', firstOfMonth)
        .lte('log_date', today)
    ).then(({ data }) => {
      if (data) setMonthAccum(data.reduce((s, r) => s + Number(r.calories_burned), 0));
    });
  }, [userId, cardioKcal]);

  const registrado = cardioKcal ?? fetched ?? 0;
  const cumplido = registrado >= meta && meta > 0;
  const faltan = Math.max(0, meta - registrado);

  // Celebrar cuando se cumple la meta de cardio del día.
  // Gated por sessionStorage → solo dispara 1ra vez del día.
  useEffect(() => {
    if (cumplido && userId) {
      celebrateOnce(`cardio-goal-${todayKey()}-${userId}`, () => {
        fireGoalConfetti();
      });
    }
  }, [cumplido, userId]);

  // Meta mensual derivada: meta diaria × días transcurridos del mes
  const now = new Date();
  const dayOfMonth = now.getDate();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const metaMonthExpected = meta * dayOfMonth;
  const metaMonthFull = meta * daysInMonth;
  const monthPct = metaMonthExpected > 0 ? Math.round((monthAccum / metaMonthExpected) * 100) : 0;

  return (
    <div className={`rounded-xl bg-primary p-5 shadow-sm ${cumplido ? 'ring-1 ring-green-500/40' : ''}`}>
      <div className="flex items-center gap-2 mb-4">
        <HeartPulse className={`h-4 w-4 shrink-0 ${cumplido ? 'text-green-400' : 'text-accent'}`} />
        <p className={`text-[11px] font-black uppercase tracking-widest ${cumplido ? 'text-green-400' : 'text-accent'}`}>
          Meta de Cardio
        </p>
        {cumplido && (
          <span className="ml-auto text-[10px] font-black text-green-400 uppercase tracking-wide">✓ Cumplida</span>
        )}
      </div>

      {/* Anillo central — mobile-first, centrado */}
      <div className="flex justify-center mb-4">
        <CardioRing current={registrado} goal={meta} size={160} stroke={14} />
      </div>

      {/* Mensaje de estado */}
      <div className="text-center mb-4">
        <p className="text-[11px] font-bold text-primary-foreground/70">
          {cumplido
            ? `🔥 +${(registrado - meta).toLocaleString()} kcal extra hoy`
            : meta > 0
              ? `Faltan ${faltan.toLocaleString()} kcal para tu meta`
              : 'Sin meta asignada'}
        </p>
      </div>

      {/* Acumulado del mes */}
      {meta > 0 && (
        <div className="border-t border-primary-foreground/10 pt-3">
          <div className="flex items-center justify-between mb-1.5">
            <p className="text-[10px] text-primary-foreground/60 font-bold uppercase tracking-wider">
              Este mes
            </p>
            <p className="text-[11px] font-black text-primary-foreground">
              {monthAccum.toLocaleString()}
              <span className="text-[10px] text-primary-foreground/60 font-bold">
                {' / '}{metaMonthFull.toLocaleString()} kcal
              </span>
            </p>
          </div>
          <div className="w-full bg-primary-foreground/10 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-500 ${monthPct >= 90 ? 'bg-green-400' : monthPct >= 60 ? 'bg-yellow-400' : 'bg-accent'}`}
              style={{ width: `${Math.min(100, (monthAccum / metaMonthFull) * 100)}%` }}
            />
          </div>
          <p className="text-[10px] text-primary-foreground/50 font-bold mt-1">
            Día {dayOfMonth} de {daysInMonth} · esperado {metaMonthExpected.toLocaleString()} kcal · {monthPct}% adherencia
          </p>
        </div>
      )}
    </div>
  );
}

export function DashboardSection({ perfil, ultimoRegistro, grasaFaltante, userId, displayName, isOwnProfile = false }: DashboardSectionProps) {
  const baselineWeight = perfil.historial[0]?.peso ?? ultimoRegistro.peso ?? 0;
  const isEstimated = perfil.historial[0]?.sesion?.includes('Estimado') ?? false;

  const formatEvalDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  // El bloque de "Próxima evaluación" + agregar al calendario se
  // movió a CoachHubCard junto con el mensaje del coach y el CTA
  // de reservar sesión. Todo vive ahora unificado para mantener
  // armonía visual.

  return (
    <section id="dashboard" className="scroll-mt-32 animate-fade-in space-y-8">

      {/* El header con foto + nombre se movió a DailyBriefingCard
          arriba del home. Aquí lo quitamos para no duplicar y mantener
          una jerarquía visual clara. */}

      {/* Hub unificado del coach: mensaje + próxima evaluación + reservar
          1-on-1. Antes eran 3 cards separadas con estilos distintos. */}
      <CoachHubCard perfil={perfil} />

      <HeroBanner perfil={perfil} grasaFaltante={grasaFaltante} isEstimated={isEstimated} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-12 stagger-children">
        <StatCard
          label="Ingesta Diaria"
          value={`${perfil.metaCalorias} kcal`}
          sub="Objetivo Calculado"
          icon={Utensils}
        />
        <StatCard
          label="Último Peso"
          value={`${ultimoRegistro.peso} kg`}
          sub={`vs ${baselineWeight} kg Inicio`}
          icon={Activity}
          highlight
        />
        <StatCard
          label="Grasa Actual"
          value={`${ultimoRegistro.grasa}%`}
          sub={isEstimated ? 'Valor Estimado' : 'Bioimpedancia Reciente'}
          icon={Target}
        />
        <StatCard
          label="IMC"
          value={ultimoRegistro.imc != null ? `${ultimoRegistro.imc}` : '--'}
          sub="Índice de Masa Corporal"
          icon={BarChart2}
        />
        <StatCard
          label="Masa Libre Grasa"
          value={ultimoRegistro.masaLibreGrasa != null ? `${ultimoRegistro.masaLibreGrasa} kg` : '--'}
          sub="Bioimpedancia Reciente"
          icon={Activity}
        />
      </div>

      <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
        <EvolutionChart data={perfil.historial} />
      </div>
    </section>
  );
}
